import { ChangeDetectionStrategy, Component, input } from '@angular/core';
import { RouterLink, RouterLinkActive } from '@angular/router';

/**
 * A single bottom-tab destination. Mirrors the admin-shell `NavItemI` shape but is
 * intentionally decoupled so this presentational component carries no business logic.
 */
export interface MobileTabI {
  /** Absolute router path the tab links to (e.g. `/auth/admin-dashboard`). */
  path: string;
  /** Visible label under the icon. */
  label: string;
  /** Boxicons class (e.g. `bx-grid-alt`). */
  icon: string;
}

/**
 * Bottom-fixed mobile tab bar for the admin shell (<=767px). Presentational only:
 * it takes the resolved tabs + active route as inputs and uses routerLink /
 * routerLinkActive for navigation + active styling — no click handlers, no state.
 */
@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'bodyanalytics-mobile-tab-bar',
  templateUrl: './mobile-tab-bar.component.html',
  styleUrls: ['./mobile-tab-bar.component.scss'],
  imports: [RouterLink, RouterLinkActive],
})
export class MobileTabBarComponent {
  /** Tabs to render along the bottom of the viewport. */
  readonly tabs = input<MobileTabI[]>([]);

  /**
   * Current route (e.g. the shell's `router.url`). Used as a deterministic active
   * fallback for the amber indicator. At runtime `routerLinkActive` also flags the
   * matching tab; this input keeps the active state testable without standing up a
   * full router navigation.
   */
  readonly activeRoute = input<string>('');

  /** True when `tab` corresponds to the current route. Drives the amber indicator. */
  isActive(tab: MobileTabI): boolean {
    const current = this.activeRoute();
    return current.length > 0 && current.startsWith(tab.path);
  }
}
